/**
 * Color utility functions for working with colors across the application
 */

/**
 * Calculate relative luminance using W3C WCAG standard
 * https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 *
 * Direct translation of app/services/color_utils.rb#color_relative_luminance
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Relative luminance value (0-1)
 */
export function calculateRelativeLuminance(r, g, b) {
  // Convert RGB values from 0-255 to 0-1
  const rSrgb = r / 255.0
  const gSrgb = g / 255.0
  const bSrgb = b / 255.0

  // Convert sRGB values to linear RGB
  const rLinear = rSrgb <= 0.03928 ? rSrgb / 12.92 : Math.pow((rSrgb + 0.055) / 1.055, 2.4)
  const gLinear = gSrgb <= 0.03928 ? gSrgb / 12.92 : Math.pow((gSrgb + 0.055) / 1.055, 2.4)
  const bLinear = bSrgb <= 0.03928 ? bSrgb / 12.92 : Math.pow((bSrgb + 0.055) / 1.055, 2.4)

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Determine if a background color is dark based on relative luminance
 *
 * @param {string} backgroundColor - CSS color string (e.g., 'rgb(255, 255, 255)')
 * @returns {boolean} True if the background is considered dark
 */
export function isBackgroundColorDark(backgroundColor) {
  // Extract RGB values from the color string
  const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (!rgbMatch) {
    return false
  }

  const [, r, g, b] = rgbMatch.map(Number)
  const relativeLuminance = calculateRelativeLuminance(r, g, b)

  return relativeLuminance < 0.5
}
